import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Hourglass, Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabase";

/**
 * KPI admin : entonnoir de l'essai Solo. Source : RPC `get_trial_funnel`
 * (admin-only, migration 20260921138000).
 *
 * **Pourquoi ce composant existe au-delà du tableau de bord.** L'inventaire du
 * 16/09 (`Docs/QUOTAS&PRICING/2026-09-16-INVENTAIRE-Appelants-Plan-Payant.md`)
 * a montré qu'aucun écran d'administration ne sait dire qui est en essai :
 * `get_pulse_users` lit `profiles.plan_type`, que l'essai ne touche pas, et
 * affiche donc « free » ; le badge du support lit l'abonnement et affiche
 * « Solo ». Les deux se contredisent. Cet écran est le seul endroit où la
 * question « combien d'essais, et que font-ils ? » reçoit une réponse.
 *
 * **Le chiffre à ne pas mélire.** `converted` compte un VRAI abonnement
 * LemonSqueezy, jamais `is_paid_plan_for_product` — ce helper répond vrai
 * pendant un essai, le taux vaudrait donc 100 % par construction.
 */

interface TrialFunnel {
  days: number;
  generated_at: string;
  started: number;
  active: number;
  ended: number;
  used_groups: number;
  used_recurring: number;
  used_either: number;
  converted: number;
  emails_sent: number;
  opted_in: number;
}

/** En deçà de ce nombre d'essais, on n'affiche aucun pourcentage. */
const SEUIL_POURCENTAGE = 10;

const FENETRES = [30, 90, 365] as const;

export const TrialFunnelKPI: React.FC = () => {
  const { t } = useTranslation("admin");
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<TrialFunnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      const { data: rows, error: rpcError } = await supabase.rpc(
        "get_trial_funnel",
        { days },
      );
      if (!active) return;
      if (rpcError) {
        setError(rpcError.message);
        setData(null);
      } else {
        setData((rows as TrialFunnel | null) ?? null);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [days]);

  // Un pourcentage sur trois essais n'informe personne et se lit pourtant comme
  // une mesure. En dessous du seuil on rend `null`, et la colonne reste vide —
  // plutôt qu'un « 33 % » qui serait cité plus tard comme un fait.
  const part = (n: number, total: number): string | null => {
    if (total < SEUIL_POURCENTAGE || total === 0) return null;
    return `${Math.round((n / total) * 100)} %`;
  };

  const etapes = data
    ? [
        { cle: "started", valeur: data.started, part: null as string | null },
        { cle: "active", valeur: data.active, part: part(data.active, data.started) },
        { cle: "ended", valeur: data.ended, part: part(data.ended, data.started) },
        {
          cle: "used_either",
          valeur: data.used_either,
          part: part(data.used_either, data.started),
        },
        {
          cle: "used_groups",
          valeur: data.used_groups,
          part: part(data.used_groups, data.started),
        },
        {
          cle: "used_recurring",
          valeur: data.used_recurring,
          part: part(data.used_recurring, data.started),
        },
        {
          cle: "converted",
          valeur: data.converted,
          part: part(data.converted, data.started),
        },
      ]
    : [];

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Hourglass className="w-5 h-5 text-amber-400" />
          {t("trial_funnel.title")}
        </h3>
        <div className="flex items-center gap-1">
          {FENETRES.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setDays(f)}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                days === f
                  ? "bg-amber-400/20 text-amber-200"
                  : "text-white/50 hover:text-white/80"
              }`}
            >
              {t("trial_funnel.window", { days: f })}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-white/40 mb-4">{t("trial_funnel.subtitle")}</p>

      {loading && (
        <div className="flex items-center gap-2 text-white/60 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("trial_funnel.loading")}
        </div>
      )}

      {error && !loading && (
        <div className="text-red-300 text-sm">
          {t("trial_funnel.error", { message: error })}
        </div>
      )}

      {!loading && !error && data && data.started === 0 && (
        <div className="text-white/60 text-sm">{t("trial_funnel.no_data")}</div>
      )}

      {!loading && !error && data && data.started > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 border-b border-white/10">
                  <th className="py-2 pr-4 font-medium">
                    {t("trial_funnel.table.step")}
                  </th>
                  <th className="py-2 pr-4 font-medium text-right">
                    {t("trial_funnel.table.count")}
                  </th>
                  <th className="py-2 font-medium text-right">
                    {t("trial_funnel.table.share")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {etapes.map((e) => (
                  <tr
                    key={e.cle}
                    className="border-b border-white/5 text-white/80"
                  >
                    <td className="py-2 pr-4">
                      {t(`trial_funnel.steps.${e.cle}`)}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {e.valeur}
                    </td>
                    <td className="py-2 text-right tabular-nums text-white/50">
                      {e.part ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/50">
            <span>
              {t("trial_funnel.emails_sent", { count: data.emails_sent })}
            </span>
            <span>{t("trial_funnel.opted_in", { count: data.opted_in })}</span>
          </div>

          {data.started < SEUIL_POURCENTAGE && (
            <p className="mt-3 text-xs text-amber-200/70">
              {t("trial_funnel.too_few", { threshold: SEUIL_POURCENTAGE })}
            </p>
          )}
        </>
      )}
    </div>
  );
};
